package co.maytes.standupbot.dashboard.features.standups

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.feedback.SkeletonList
import co.maytes.standupbot.dashboard.components.motion.StateSwap
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.SectionCard
import co.maytes.standupbot.dashboard.components.widgets.SlackMessagePreview
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.features.templates.ListPhase
import co.maytes.standupbot.dashboard.features.templates.TemplatePreviewPane
import co.maytes.standupbot.dashboard.features.templates.rememberTemplatesStore
import co.maytes.standupbot.dashboard.features.templates.viewPhase
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.standup.StandupDto
import co.maytes.standupbot.shared.dto.standup.UpdateStandupRequestDto
import co.maytes.standupbot.shared.dto.template.TemplateDto
import co.maytes.standupbot.shared.template.BuiltInTemplate
import co.maytes.standupbot.shared.template.BuiltInTemplates
import co.maytes.standupbot.shared.template.SectionsConfigDto
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.ariaHidden
import com.varabyte.kobweb.compose.ui.modifiers.ariaLabel
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onKeyDown
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.modifiers.tabIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch

const val TEMPLATE_DEFAULT_KEY = "__default__"

private const val WORKSPACE_DEFAULT_SELECTION = "default"

private const val DEFAULT_KEY = TEMPLATE_DEFAULT_KEY

@Composable
fun TemplateTab(
    standup: StandupDto,
    workspaceId: String,
    teamsStore: StandupsStore,
) {
    val templates = rememberTemplatesStore()
    val ui = rememberUiStore()
    val ctx = rememberPageContext()
    val auth = rememberAuthStore()
    val scope = rememberCoroutineScope()
    val workspaceName = auth.currentUser?.workspace?.name

    val current = teamsStore.cachedDetail(standup.id) ?: standup
    var selectedKey by remember(current.templateId) {
        mutableStateOf(current.templateId ?: DEFAULT_KEY)
    }
    var saving by remember { mutableStateOf(false) }

    LaunchedEffect(workspaceId) {
        if (templates.list.isEmpty() && templates.phase != ListPhase.Loading) {
            templates.refresh(workspaceId)
        }
    }

    val selectedTemplate: TemplateDto? = templates.list.firstOrNull { it.id == selectedKey }
    val selectedBuiltIn: BuiltInTemplate? = BuiltInTemplates.find(selectedKey)
    val defaultTemplate: TemplateDto? = templates.list.firstOrNull { it.isDefault }
    val previewTemplate: TemplateDto? = selectedTemplate ?: defaultTemplate.takeIf { selectedKey == DEFAULT_KEY }
    val previewBody = previewTemplate?.promptBody.orEmpty()
    val previewSections = previewTemplate?.sectionsConfig ?: selectedBuiltIn?.sections ?: SectionsConfigDto.DEFAULT
    val dirty = (current.templateId ?: DEFAULT_KEY) != selectedKey

    SectionCard(
        title = "Template",
        description = "Sections and headings apply to your posted standup. The prompt text of a custom template is a preview only for now.",
    ) {
      Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(Spacing.md),
      ) {
        StateSwap(state = templates.phase.viewPhase()) {
            when (templates.phase) {
                ListPhase.Idle, ListPhase.Loading -> SkeletonList(rows = 3)
                ListPhase.Failed -> SpanText(
                    text = "Could not load templates: ${templates.lastError ?: "unknown error"}",
                    modifier = Modifier
                        .fontSize(Typography.bodySmall.size)
                        .color(Palette.error),
                )
                ListPhase.Ready -> TemplatePickerGrid(
                    templates = templates.list,
                    selectedKey = selectedKey,
                    onSelect = { selectedKey = it },
                )
            }
        }

        current.templateName?.let {
            SpanText(
                text = "Posts with: $it",
                modifier = Modifier
                    .fontSize(Typography.bodySmall.size)
                    .fontWeight(500)
                    .color(Palette.textSecondary),
            )
        }

        if (previewBody.isBlank()) {
            SlackMessagePreview(layout = previewSections, modifier = Modifier.fillMaxWidth())
        } else {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(Spacing.sm),
            ) {
                SpanText(
                    text = "Preview",
                    modifier = Modifier
                        .fontSize(Typography.caption.size)
                        .fontWeight(600)
                        .color(Palette.textSecondary),
                )
                TemplatePreviewPane(
                    promptBody = previewBody,
                    sectionsConfig = previewSections,
                    workspaceName = workspaceName,
                )
            }
        }

        Row(
            modifier = Modifier.fillMaxWidth().gap(Spacing.sm),
            horizontalArrangement = Arrangement.End,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            if (selectedTemplate != null && !selectedTemplate.isDefault) {
                Button(
                    label = "Edit template…",
                    variant = ButtonVariant.Ghost,
                    onClick = { ctx.router.navigateTo("/app/templates/${selectedTemplate.id}") },
                )
            }
            Button(
                label = if (saving) "Saving…" else "Save",
                variant = ButtonVariant.Primary,
                enabled = dirty && !saving,
                onClick = {
                    scope.launch {
                        saving = true
                        val newTemplateId = if (selectedKey == DEFAULT_KEY) WORKSPACE_DEFAULT_SELECTION else selectedKey
                        val res = teamsStore.update(
                            workspaceId = workspaceId,
                            standupId = standup.id,
                            patch = UpdateStandupRequestDto(templateId = newTemplateId),
                        )
                        saving = false
                        when (res) {
                            is Result.Ok -> ui.showToast("Template updated.", ToastKind.Success)
                            is Result.Err -> ui.showToast(
                                "Save failed: ${res.error.displayMessage}",
                                ToastKind.Error,
                            )
                        }
                    }
                },
            )
        }
      }
    }
}

@Composable
fun TemplatePickerGrid(
    templates: List<TemplateDto>,
    selectedKey: String,
    onSelect: (String) -> Unit,
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(Spacing.sm),
    ) {
        PickerCard(
            active = selectedKey == DEFAULT_KEY,
            title = "Workspace default",
            subtitle = "Uses the workspace default template, or Classic when none is set.",
            badge = null,
            onClick = { onSelect(DEFAULT_KEY) },
        )
        BuiltInTemplates.all.forEach { builtIn ->
            PickerCard(
                active = selectedKey == builtIn.key,
                title = builtIn.name,
                subtitle = builtIn.description,
                badge = "Built-in",
                onClick = { onSelect(builtIn.key) },
            )
        }
        templates.forEach { tpl ->
            PickerCard(
                active = selectedKey == tpl.id,
                title = tpl.name,
                subtitle = tpl.description.orEmpty().ifBlank { "No description" },
                badge = if (tpl.isDefault) "Default" else null,
                onClick = { onSelect(tpl.id) },
            )
        }
    }
}

@Composable
private fun PickerCard(
    active: Boolean,
    title: String,
    subtitle: String,
    badge: String?,
    onClick: () -> Unit,
) {
    val bg = if (active) Palette.accentSubtle else Palette.surface
    val borderColor = if (active) Palette.accent else Palette.border
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(bg)
            .borderRadius(Radius.md)
            .padding(Spacing.md)
            .cursor(Cursor.Pointer)
            .role("radio")
            .tabIndex(0)
            .ariaLabel(title)
            .attrsModifier { attr("aria-checked", active.toString()) }
            .onClick { onClick() }
            .onKeyDown { e ->
                if (e.key == "Enter" || e.key == " ") {
                    e.preventDefault()
                    onClick()
                }
            }
            .styleModifier {
                property("border", "1px solid ${borderColor.cssRgb()}")
                property("transition", Motion.TRANSITION_HOVER)
            },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(Spacing.md),
    ) {
        Column(
            modifier = Modifier.styleModifier { property("flex", "1 1 auto") },
            verticalArrangement = Arrangement.spacedBy(Spacing.xxs),
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(Spacing.xs),
            ) {
                SpanText(
                    text = title,
                    modifier = Modifier
                        .fontSize(Typography.body.size)
                        .fontWeight(600)
                        .color(Palette.textPrimary),
                )
                if (badge != null) Badge(label = badge, tone = BadgeTone.Accent)
            }
            SpanText(
                text = subtitle,
                modifier = Modifier
                    .fontSize(Typography.caption.size)
                    .color(Palette.textTertiary)
                    .styleModifier { property("line-height", "1.5") },
            )
        }
        SpanText(
            text = if (active) "●" else "○",
            modifier = Modifier
                .ariaHidden()
                .fontSize(Typography.body.size)
                .color(if (active) Palette.accent else Palette.textTertiary),
        )
    }
}
